import type { ComponentPropsWithoutRef } from "react"
import { forwardRef, memo } from "react"
import { Link } from "@tanstack/react-router"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import type { Contact } from "@productlane/db/browser"
import { cn } from "@productlane/lib/src/browser"
import { Avatar, IconButton } from "@productlane/ui"

import { useRole } from "@/lib/useRole"

interface ContactTagProps extends ComponentPropsWithoutRef<"div"> {
	contact: Pick<Contact, "id" | "name" | "email" | "imageUrl">
	onUnlink?: () => void
}

export const ContactTag = memo(
	forwardRef<HTMLDivElement, ContactTagProps>(
		({ contact, onUnlink, className, ...props }, ref) => {
			const { role } = useRole()
			return (
				<div
					ref={ref}
					className={cn(
						"group -mx-4 flex select-none items-center gap-2 truncate rounded-lg px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-800",
						className,
					)}
					{...props}
				>
					<Avatar
						name={contact.name || contact.email}
						image={contact?.imageUrl}
						size="s"
						roundness="full"
					/>
					<Link
						to="/contacts/$id"
						params={{ id: contact.id }}
						className="flex flex-1 flex-row gap-2 truncate text-sm"
					>
						<span className="max-w-80 truncate">{contact.email}</span>
						{contact.name && <span className="text-primary">•</span>}
						{contact.name && (
							<div className="max-w-80 truncate text-secondary">
								{contact.name}
							</div>
						)}
					</Link>
					{role !== "VIEWER" && onUnlink && (
						<IconButton
							variant="ghost"
							size="sm"
							Icon={X}
							className="text-secondary opacity-0 group-hover:opacity-100"
							onClick={onUnlink}
						/>
					)}
				</div>
			)
		},
	),
)
